import { action } from '@ember/object';
import { next, schedule, scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import { capitalize } from '@ember/string';
import { htmlSafe } from '@ember/template';
import { cached, tracked } from '@glimmer/tracking';

import DOMPurify from 'dompurify';

import type { APIData, LazyLoadSelectors, Service, SortOrderDirection } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { generateWordsArray, sortBy } from 'webapp/utils/common';
import type { SearchProps, SortProps } from 'webapp/utils/filter';
import { getCompactString, searchAndSortByRelevance } from 'webapp/utils/filter';

import type { FilterOption, FilterSettings } from 'webapp/models-custom/filter-sort/filter';
import Filter, { RECENTLY_DELETED_ID } from 'webapp/models-custom/filter-sort/filter';
import Serviced from 'webapp/models-custom/serviced';

export type FilterIdType = string | number;
export type FilterSelectedValue = FilterIdType[];

type GetAllItemsFn<T> = () => T[];
type AfterFilterFn = () => void;
type BeforeFilterFn = () => void;
type OnOpenViewFn = (viewId: string) => void;

export interface ItemTypeBase {
	id?: string | number | null;
	activeFilter?: string[];
	get?: (value: string) => unknown;
}

export interface FilterSortSettings<T> {
	getAllItems: GetAllItemsFn<T>;
	afterFilter?: AfterFilterFn;
	beforeFilter?: BeforeFilterFn;
	onOpenView?: OnOpenViewFn;
	noResultsMiddleText?: string;
	selectors?: LazyLoadSelectors;
	// read more about these properties in utils/filter.ts
	prioritizeExactMatches?: boolean;
	additionalSorts?: ((a: T, b: T) => number)[];
}

export default class FilterSort<ItemType extends ItemTypeBase> extends Serviced {
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('view-settings-manager')
	declare viewSettingsManagerService: Service.ViewSettingsManager;

	@tracked filters: Filter<ItemType, FilterIdType>[] = [];
	@tracked prioritizeExactMatches: boolean;
	additionalSorts: ((a: ItemType, b: ItemType) => number)[] = [];

	@tracked sortProp = 'id';
	@tracked sortOrder: SortOrderDirection = 'asc';

	getAllItems: GetAllItemsFn<ItemType>;
	afterFilter: AfterFilterFn | null = null;
	beforeFilter: BeforeFilterFn | null = null;
	onOpenView: OnOpenViewFn | null = null;

	@tracked searchPlaceholder = 'Search...';
	searchProps: SearchProps = { id: 1 }; // obj with search relevance weights

	@tracked viewId = '';
	viewSelectorStyle = 'tooltip';

	trackingObjectName = '';
	typedFilterEventName: TrackingEvent | '' = '';
	typedFilterEventData: APIData.Tracking.Data = {};

	noResultsMiddleText = '';

	settingsNamePrefix = ''; // if set, saving state in localstorage (under given key + space id)
	saveFilterInUrlHash = false; // if set, saving state in url hash

	itemDomSelector = '';

	selectors: LazyLoadSelectors | null = null; // Used in Common::List::LazyLoad

	constructor(options: FilterSortSettings<ItemType>) {
		super();

		this.getAllItems = options.getAllItems;
		this.prioritizeExactMatches = options.prioritizeExactMatches ?? true;
		this.additionalSorts = options.additionalSorts || [];

		if (options.afterFilter) {
			this.afterFilter = options.afterFilter;
		}

		if (options.beforeFilter) {
			this.beforeFilter = options.beforeFilter;
		}

		if (options.onOpenView) {
			this.onOpenView = options.onOpenView;
		}

		if (options.noResultsMiddleText) {
			this.noResultsMiddleText = options.noResultsMiddleText;
		}

		if (options.selectors) {
			this.selectors = options.selectors;
		}

		next(this, this.updateActiveFilterStringOnResults, []);
	}

	get allItems() {
		return this.getAllItems();
	}

	get hasItems(): boolean {
		return this.allItems.length > 0;
	}

	get doSearchPropsHaveDifferingRelevance(): boolean {
		return Object.values(this.searchProps).some((val) => val !== 1);
	}

	@action
	changeSortProp(value: string) {
		this.sortProp = value;
	}

	@action
	changeSortOrder(value: SortOrderDirection) {
		this.sortOrder = value;
	}

	// --------- Search ---------

	@tracked _typedFilter = '';

	get typedFilter(): string {
		return this._typedFilter;
	}

	set typedFilter(value: string) {
		this._typedFilter = value;
		this.isSortBySearchPointsArray = !!value;

		const values = generateWordsArray(value.toLowerCase()),
			sendTrackingEvent = value === value.toLowerCase();

		this.updateActiveFilterStringOnResults(values);

		// tracking search event
		if (this.typedFilterEventName && value.length >= 3) {
			let eventData = {
				searchTerm: value,
			};

			if (Object.keys(this.typedFilterEventData)) {
				eventData = { ...eventData, ...this.typedFilterEventData };
			}

			if (sendTrackingEvent) {
				// Hack because typedFilterChanged in app\components\input-search.js sets typedFilter to non-lowercase string from the outside (ex: Metric Library quickSearchOptions)
				this.analyticsService.event(this.typedFilterEventName, eventData);
			}
		}
	}

	get isSearchActive(): boolean {
		return !!this.typedFilter;
	}

	resetSearch(): void {
		this.typedFilter = '';
	}

	updateActiveFilterStringOnResults(value: string[]): void {
		// updating "marked" on each model
		this.allItems.forEach((item) => {
			item.activeFilter = value;
		});
	}

	// --------- Saving / reading settings ---------

	get settingsName(): string {
		let settingsName = '';
		if (this.settingsNamePrefix) {
			settingsName = `${this.settingsNamePrefix}-${this.accountService.id}`;
		}
		return settingsName;
	}

	onFilterSelectionChange(filter: Filter<ItemType, FilterIdType>): void {
		this.saveSelectedFilters();

		if (filter.isTag && !filter.isDefaultSelected) {
			let origin = this.trackingObjectName;
			if (origin === 'board') {
				origin = 'home';
			}
			if (origin) {
				this.analyticsService.event(TrackingEvent.TAGS_FILTERED, { origin });
			}
		}
	}

	readSavedSelectedFilters(): (FilterIdType[] | undefined)[] {
		if (this.accountService.isDemoAccount) {
			return [undefined];
		}

		let savedValues: Partial<Record<string, FilterIdType | FilterIdType[]>> = {};

		if (this.saveFilterInUrlHash) {
			decodeURIComponent(window.location.hash)
				.substring(1)
				.split('|')
				.forEach((hash, index) => {
					if (hash) {
						savedValues[`filter${index + 1}Id`] = hash;
					}
				});
		} else if (this.settingsName) {
			savedValues =
				this.viewSettingsManagerService.load<Record<string, FilterIdType | FilterIdType[]>>(
					this.settingsName,
					true,
				) || {};
		}

		const selectedFilters: (FilterIdType[] | undefined)[] = [];
		Object.keys(savedValues).forEach((key) => {
			if (key.startsWith('filter')) {
				const id = parseInt(key.substring(6, key.length - 2), 10);
				const value = savedValues[key];

				if (value) {
					if (Array.isArray(value)) {
						selectedFilters[id] = value;
					} else {
						// read legacy single values into array and also add support for comma separated values in URL hash
						const valueArray =
							typeof value === 'string' && value.includes(',')
								? value.split(',')
								: [value];
						// convert numeric strings back to numbers to maintain proper types
						selectedFilters[id] = valueArray.map((item: string | number) => {
							// if item is already a number, return it as is
							if (typeof item === 'number') {
								return item;
							}
							// for strings, trim and try to convert to number
							const trimmed = item.trim();
							const numValue = Number(trimmed);
							return !isNaN(numValue) && trimmed !== '' && isFinite(numValue)
								? numValue
								: trimmed;
						});
					}
				}
			}
		});

		return selectedFilters;
	}

	saveSelectedFilters(): void {
		if (this.accountService.isDemoAccount) {
			return;
		}

		const settings: Record<string, FilterIdType[]> = {};
		this.filters.forEach((f) => {
			settings[`filter${f.id}Id`] = f.selected;
		});

		if (this.saveFilterInUrlHash) {
			const hashes: string[] = [];
			let anyNotDefault = false;
			sortBy(this.filters, 'id').forEach((f) => {
				const selectedString = f.isMultiSelect
					? f.selected.join(',')
					: String(f.selected[0] || '');
				const hash = this.getCompactString(selectedString);
				hashes.push(hash);
				if (!f.isDefaultSelected) {
					anyNotDefault = true;
				}
			});

			if (anyNotDefault) {
				window.location.hash = hashes.join('|');
			} else {
				window.location.hash = '';
			}
		} else if (this.settingsName) {
			this.viewSettingsManagerService.save(this.settingsName, settings, true);
		}
	}

	getCompactString(string: string): string {
		return getCompactString(string);
	}

	// --------- Sort stuff ---------

	get hideSort(): boolean {
		return (
			this.allItems.length < 2 ||
			this.isSearchActive ||
			this.isSortBySearchPointsArray ||
			this.isAnyFilterActive ||
			this.accountService.isDemoAccount
		);
	}

	// --------- View stuff ---------

	get viewOptions(): FilterOption<FilterIdType>[] {
		return [];
	}

	// default value of view selector style is 'tooltip'
	get hasViewSelectorText(): boolean {
		return this.viewSelectorStyle === 'text';
	}

	get hideViewSelector(): boolean {
		return this.allItems.length === 0;
	}

	get hideSearch(): boolean {
		return false;
	}

	@action
	openView(viewId: string): void {
		this.viewId = viewId;

		const trackingObjName = this.trackingObjectName;

		if (trackingObjName) {
			let toTrack = '';
			if (trackingObjName === 'board') {
				toTrack = 'Databoards';
			} else {
				toTrack = capitalize(trackingObjName);
			}

			this.analyticsService.event(`${toTrack} changed view`, { view: viewId });
		}

		schedule('afterRender', () => {
			$(window).trigger('appDidTransition');
		});

		if (this.onOpenView) {
			this.onOpenView(viewId);
		}
	}

	// --------- Filter stuff ---------

	get isAnyFilterActive(): boolean {
		// when filter has recently deleted option, it is always active to exclude recently deleted items from "All"
		return this.filters.some((f) => f.isActive && !f.hasRecentlyDeletedOption);
	}

	get isAnyFilterShown(): boolean {
		return this.filters.some((f) => f.isShown);
	}

	get isAnyFilterSort(): boolean {
		return this.filters.some((f) => f.isSort);
	}

	get isAnyNonSortFilterActive(): boolean {
		return this.filters.some((f) => !f.isSort && f.isActive);
	}

	get isRecentlyDeletedSelected(): boolean {
		return this.filters.some((filter) => filter.selected.includes(RECENTLY_DELETED_ID));
	}

	createFilter<IdType extends FilterIdType>(
		options:
			| Omit<FilterSettings<ItemType, IdType>, 'onSelectedChange'>
			| FilterSettings<ItemType, IdType>,
	) {
		const onSelectedChange = (filter: Filter<ItemType, IdType>) => {
			const filterSettingsOptions = { ...(options as FilterSettings<ItemType, IdType>) };

			if (filterSettingsOptions?.onSelectedChange) {
				filterSettingsOptions.onSelectedChange(filter);
			}

			this.onFilterSelectionChange(filter as unknown as Filter<ItemType, FilterIdType>);
		};

		const filter = new Filter<ItemType, IdType>({ ...options, onSelectedChange });

		this.filters.push(filter as unknown as Filter<ItemType, FilterIdType>);

		return filter;
	}

	findFilter(id: number): Filter<ItemType, FilterIdType> | null {
		return this.filters.find((item) => item.id === id) || null;
	}

	@action
	resetFilters(ignoreSortFilters = false): void {
		this.filters.forEach((f) => {
			if (ignoreSortFilters && f.isSort) {
				return;
			}

			f.selectOption(f.defaultValue);
		});
	}

	useFilters(allItems: ItemType[]): ItemType[] {
		let results = allItems;

		this.filters.forEach((f) => {
			if (f.isActive) {
				results = f.use(results);
			}
		});

		return results;
	}

	// --------- Result filtering ---------

	@tracked isSortBySearchPointsArray = false; // Ignore SortProps when true. Set to true when user searches something (typedFilter is changed), overwritten if the user manually switches the SortProps after the search is done

	updateSortBy(_sortBy: string): void {
		/* empty */
	}

	onSortFilterChange(selected: string) {
		this.updateSortBy(selected);
		this.isSortBySearchPointsArray = false;
	}

	get sortProps(): SortProps {
		return {
			prop: this.sortProp,
			direction: this.sortOrder,
		};
	}

	@cached
	get filteredItems(): ItemType[] {
		return this.useFilters(this.allItems);
	}

	@cached
	get results(): ItemType[] {
		// CASES:
		// 1. At first load search props are different (than null) so just ordering of items happen
		// 2. When searching, sorting is disabled (because items are sorted by relevance of weights) - Same happens while making text changes on item in list
		// 3. While clicking on TableSorter component we perform sorting of items only by wanted prop
		// 4. When clearing up search we perform sorting by default sort prop

		let results = this.filteredItems;
		const term = this.typedFilter || '',
			usedSortProps = this.isSortBySearchPointsArray ? null : this.sortProps;

		results = searchAndSortByRelevance(
			results,
			this.searchProps,
			usedSortProps,
			term,
			this.prioritizeExactMatches,
			this.additionalSorts,
		);

		// Sync activeFilter on result items so highlights survive model instance recreation
		const filterValues = term ? generateWordsArray(term.toLowerCase()) : [];
		for (const item of results) {
			item.activeFilter = filterValues;
		}

		if (this.beforeFilter) {
			next(this, this.beforeFilter);
		}

		if (this.afterFilter) {
			scheduleOnce('afterRender', this, this.afterFilter);
		}

		return results;
	}

	get noResults(): boolean {
		return !this.results.length;
	}

	@cached
	get noResultsText(): string {
		return this.getNoResultsText();
	}

	@cached
	get safeNoResultsText() {
		return htmlSafe(
			DOMPurify.sanitize(this.noResultsText, {
				ALLOWED_TAGS: ['span'],
				ALLOWED_ATTR: ['class'],
			}),
		);
	}

	@cached
	get noResultsTextWithoutCategories(): string {
		return this.getNoResultsText(true);
	}

	getNoResultsText(skipCategoriesTexts = false): string {
		let text = '';

		if (this.noResults) {
			const categoriesTexts: string[] = [];

			if (!skipCategoriesTexts) {
				this.filters.forEach((f) => {
					if (f.isActive) {
						const t = f.selectedDisplayText;
						if (t) {
							categoriesTexts.push(`${f.name} <span>${t}</span>`);
						}
					}
				});
			}

			// put text together
			if (this.typedFilter) {
				text = `No results found ${this.noResultsMiddleText}for <span>${this.typedFilter}</span>`;
				if (categoriesTexts.length) {
					text += ` with ${categoriesTexts.join(' and ')}.`;
				} else {
					text += '. Please try a different search.';
				}
			} else if (categoriesTexts.length) {
				text = `No results found ${this.noResultsMiddleText}for ${categoriesTexts.join(' and ')}.`;
			}
		}

		return text;
	}
}

// Helper type to accept any FilterSort where ItemType extends ItemTypeBase
// This works around TypeScript's invariant generics by using a type that
// accepts any FilterSort instance (since all FilterSort types share the same structure)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyFilterSort = FilterSort<any>;
